import { Component, computed, effect, inject, signal, untracked, ViewChild } from '@angular/core';
import { GoBackComponent } from 'shared/components/go-back/go-back.component';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { KorviuListComponent } from 'shared/components/korviu-list/korviu-list.component';
import { AuthService, NavigationService } from 'core';
import { EmptyTemplateDirective, ThousandsSeparatorPipe } from 'shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IKorviuListConfig, IKorviuListHighlightedItem, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { ISortSection } from 'shared/types/sort-section.interface';
import { SortOrders } from 'shared/types/sort-order.enum';
import { IFilterSection } from 'shared/types/filter-section.interface';
import { map, Observable, tap } from 'rxjs';
import { BalanceService } from 'core/services/balance.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ActivatedRoute, Router } from '@angular/router';
import { ContactUsModalComponent } from '../../../shared/modal/contact-us-modal/contact-us-modal.component';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { ShareGanasModalComponent } from '../../../shared/modal/share-ganas-modal/share-ganas-modal.component';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';
import { KorviuDatePickerComponent } from 'shared/components/korviu-date-picker/korviu-date-picker.component';
import { FormControl } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { differenceInCalendarDays, format, isFuture } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import {
  DATE_HELPER_SERVICE_FACTORY,
  DateHelperService,
  en_US,
  es_ES,
  NZ_DATE_LOCALE,
  NzI18nService
} from 'ng-zorro-antd/i18n';
import { TransactionStatusModalComponent } from '../transaction-status-modal/transaction-status-modal.component';
import { ITransaction } from 'core/types/transaction.interface';
import { IBalanceTransactionsResponse, IBalanceTransactionsSummary } from 'core/types/balance-transaction.interface';
import { canOpenTransactionDetails } from 'shared/components/korviu-list/transaction-list/transaction-list.component';
import { transactionTypeFilters } from 'shared/utils/balance-transaction-filters';

@Component({
  selector: 'app-transactions',
  imports: [
    GoBackComponent,
    KorviuButtonComponent,
    KorviuListComponent,
    ThousandsSeparatorPipe,
    TranslatePipe,
    BreadcrumbComponent,
    EmptyTemplateDirective,
    KorviuDatePickerComponent
  ],
  providers: [
    NzI18nService,
    { provide: NZ_DATE_LOCALE, useValue: enUS },
    { provide: DateHelperService, useFactory: DATE_HELPER_SERVICE_FACTORY }
  ],
  templateUrl: './transactions.component.html',
  styleUrl: './transactions.component.scss'
})
export class TransactionsComponent {
  @ViewChild('transactionList') public transactionList: KorviuListComponent;

  readonly #authService = inject(AuthService);
  readonly #balanceService = inject(BalanceService);
  readonly #modalService = inject(NzModalService);
  readonly #translateService = inject(TranslateService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #navigationService = inject(NavigationService);
  readonly #dateI18n = inject(NzI18nService);

  readonly language = toSignal(this.#translateService.onLangChange.pipe(map(event => event.lang)), {
    initialValue: this.#translateService.currentLang || this.#translateService.defaultLang || 'es'
  });
  readonly emptyMessageKey = signal('balance.youHaventMadeAnyTransactionsYet');

  readonly startDateControl = new FormControl<Date | null>(null);
  readonly endDateControl = new FormControl<Date | null>(null);

  startDateSignal = toSignal(this.startDateControl.valueChanges);
  endDateSignal = toSignal(this.endDateControl.valueChanges);

  public transactionListConfig: IKorviuListConfig;

  readonly transactionSummary = signal<IBalanceTransactionsSummary>({
    total_spent: 0,
    total_earned: 0,
    balance: 0,
    potential_balance: 0,
    spendable: 0,
    deficit: 0,
    in_deficit: false
  });
  readonly currentUser = computed(() => this.#authService.currentUser$());
  readonly isRewardBased = computed(() => this.currentUser()?.isRewardBased);
  readonly currentBalance = computed(() => this.transactionSummary().balance);
  readonly growingBalance = computed(() => this.transactionSummary().potential_balance);
  readonly spentBalance = computed(() => this.transactionSummary().total_spent);
  readonly moneyBalance = computed(() => this.currentUser()?.moneyBalance);

  public balanceSortSections: ISortSection[] = [
    {
      name: this.#translateService.instant('common.sortOption.date'),
      localizableName: 'common.sortOption.date',
      options: [
        {
          name: this.#translateService.instant('common.sortOption.newest'),
          localizableName: 'common.sortOption.newest',
          sortDirection: { sortBy: 'event_date', sortOrder: SortOrders.DESCEND },
          selectedByDefault: true
        },
        {
          name: this.#translateService.instant('common.sortOption.oldest'),
          localizableName: 'common.sortOption.oldest',
          sortDirection: { sortBy: 'event_date', sortOrder: SortOrders.ASCEND }
        }
      ]
    },
    {
      name: this.#translateService.instant('common.sortOption.ganasAmount'),
      localizableName: 'common.sortOption.ganasAmount',
      options: [
        {
          name: this.#translateService.instant('common.sortOption.highest'),
          localizableName: 'common.sortOption.highest',
          sortDirection: { sortBy: 'amount', sortOrder: SortOrders.DESCEND }
        },
        {
          name: this.#translateService.instant('common.sortOption.lowest'),
          localizableName: 'common.sortOption.lowest',
          sortDirection: { sortBy: 'amount', sortOrder: SortOrders.ASCEND }
        }
      ]
    },
    {
      name: this.#translateService.instant('common.sortOption.name'),
      localizableName: 'common.sortOption.name',
      options: [
        {
          name: this.#translateService.instant('common.sortOption.A-Z'),
          localizableName: 'common.sortOption.A-Z',
          sortDirection: { sortBy: 'name', sortOrder: SortOrders.ASCEND }
        },
        {
          name: this.#translateService.instant('common.sortOption.Z-A'),
          localizableName: 'common.sortOption.Z-A',
          sortDirection: { sortBy: 'name', sortOrder: SortOrders.DESCEND }
        }
      ]
    }
  ];

  public balanceFilterSections: IFilterSection[] = [
    {
      name: this.#translateService.instant('common.sortOption.type'),
      localizableName: 'common.sortOption.type',
      parameter_name: 'status',
      checkboxModel: [
        {
          label: this.#translateService.instant('common.sortOption.deposit'),
          localizableName: 'common.sortOption.deposit',
          value: 'Deposit',
          parameter_name: 'Deposit'
        },
        {
          label: this.#translateService.instant('common.sortOption.failedTransaction'),
          localizableName: 'common.sortOption.failedTransaction',
          value: 'Failed',
          parameter_name: 'Failed'
        },
        {
          label: this.#translateService.instant('common.sortOption.pending'),
          localizableName: 'common.sortOption.pending',
          value: 'Pending',
          parameter_name: 'Pending'
        },
        {
          label: this.#translateService.instant('common.sortOption.redeemed'),
          localizableName: 'common.sortOption.redeemed',
          value: 'Redeemed',
          parameter_name: 'Redeemed'
        },
        {
          label: this.#translateService.instant('common.sortOption.purchases'),
          localizableName: 'common.sortOption.purchases',
          value: 'Purchases',
          parameter_name: 'Purchases'
        },
        {
          label: this.#translateService.instant('common.sortOption.shared'),
          localizableName: 'common.sortOption.shared',
          value: 'Shared',
          parameter_name: 'Shared'
        }
      ]
    }
  ];

  constructor() {
    this._initializeFields();

    effect(() => {
      const spanish = this.language().toLowerCase().startsWith('es');
      this.#dateI18n.setDateLocale(spanish ? es : enUS);
      this.#dateI18n.setLocale(spanish ? es_ES : en_US);
    });
    this.#translateService.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => this.transactionList?.reloadData());

    effect(() => {
      const startDate = this.startDateSignal();
      const endDate = this.endDateSignal();

      untracked(() => {
        if (startDate !== undefined || endDate !== undefined) {
          this.transactionList?.reloadData(true);
        }
      });
    });
  }

  public goback() {
    this.#navigationService.goBack();
  }

  public onShareClick(): void {
    this._openSharGanasModal();
  }

  public onRedeemClick(): void {
    this.#router.navigate(['workspace', 'rewards']);
  }

  public showContactUsForm(): void {
    this.#modalService.create({
      nzContent: ContactUsModalComponent,
      nzCentered: true,
      nzClosable: false,
      nzWidth: 554,
      nzWrapClassName: 'modal-wrap'
    });
  }

  public disableStartDate = (currentDate: Date): boolean => {
    if (!currentDate) return false;
    if (isFuture(currentDate)) return true;
    const end = this.endDateControl.value as Date;
    if (end) {
      return differenceInCalendarDays(currentDate, end) > 0;
    }
    return false;
  };

  public disableEndDate = (currentDate: Date): boolean => {
    if (!currentDate) return false;
    if (isFuture(currentDate)) return true;

    const start = this.startDateControl.value as Date;
    if (start) {
      return differenceInCalendarDays(currentDate, start) < 0;
    }
    return false;
  };

  public onTransactionClick(transaction: ITransaction): void {
    if (!canOpenTransactionDetails(transaction)) {
      return;
    }

    this.#modalService.create({
      nzContent: TransactionStatusModalComponent,
      nzWrapClassName: 'modal-wrap',
      nzClosable: false,
      nzWidth: 555,
      nzClassName: 'modal-dialog',
      nzData: { transaction: transaction }
    });
  }

  private _initializeFields(): void {
    const queryParams = this.#route.snapshot.queryParams;
    const filters: IFilter[] = [];
    const highlightedItem = this._getHighlightedItem(queryParams);

    if (typeof queryParams?.['activityDate'] === 'string') {
      const activityDate = new Date(`${queryParams['activityDate']}T00:00:00`);

      if (!Number.isNaN(activityDate.getTime())) {
        this.startDateControl.setValue(activityDate);
        this.endDateControl.setValue(activityDate);
      }
    }

    if (queryParams?.['status']) {
      const requestedStatus = String(queryParams['status']).toLowerCase();
      const status = this.balanceFilterSections[0].checkboxModel.find(
        option => option.parameter_name.toLowerCase() === requestedStatus
      )?.parameter_name;

      if (status) {
        filters.push({ name: 'status', value: status });
      }
    }

    this.transactionListConfig = {
      getFn: this._getTransactionList.bind(this),
      filterSections: this.balanceFilterSections,
      sortSections: this.balanceSortSections,
      itemType: KorviuListItemTypes.TRANSACTION,
      totalLocalizableName: 'balance.transactions',
      detailsFn: this.onTransactionClick.bind(this),
      hideSearch: true,
      filters: filters.length > 0 ? filters : undefined,
      highlightedItem,
      pagination: highlightedItem ? { page: 1, pageSize: 100 } : undefined
    };
  }

  private _getHighlightedItem(queryParams: Record<string, unknown>): IKorviuListHighlightedItem | undefined {
    const transactionId = Number(queryParams?.['transactionId']);

    if (Number.isInteger(transactionId) && transactionId > 0) {
      return { field: 'id', value: transactionId };
    }

    const purchaseId = Number(queryParams?.['purchaseId']);

    if (Number.isInteger(purchaseId) && purchaseId > 0) {
      return { field: 'purchaseId', value: purchaseId };
    }

    const earnedMonth = queryParams?.['earnedMonth'];

    if (typeof earnedMonth === 'string' && /^\d{4}-\d{2}$/.test(earnedMonth)) {
      return { field: 'earnedMonth', value: earnedMonth };
    }

    return undefined;
  }

  private _getTransactionList(
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting
  ): Observable<IBalanceTransactionsResponse> {
    const startDate = this.startDateSignal();
    const endDate = this.endDateSignal();

    filters = [...filters];

    if (startDate) {
      filters.push({ value: format(new Date(startDate), 'yyyy-MM-dd'), name: 'start_date' });
    }

    if (endDate) {
      filters.push({ value: format(new Date(endDate), 'yyyy-MM-dd'), name: 'end_date' });
    }

    const normalizedSorting =
      sorting?.sortBy === 'name'
        ? {
            ...sorting,
            sortBy: `${sorting.sortBy}_${this.#translateService?.currentLang}`
          }
        : sorting;

    return this.#balanceService
      .getBalanceTransactions(pagination, transactionTypeFilters(filters), normalizedSorting)
      .pipe(
        tap(response => {
          this.transactionSummary.set(response.summary);
          this.emptyMessageKey.set(
            filters.length ? 'balance.noTransactionsMatchFilters' : 'balance.youHaventMadeAnyTransactionsYet'
          );
        })
      );
  }

  private _openSharGanasModal(): void {
    this.#modalService.create({
      nzContent: ShareGanasModalComponent,
      nzWrapClassName: 'modal-wrap',
      nzClosable: false,
      nzWidth: 555,
      nzClassName: 'modal-dialog',
      nzData: { transactionList: this.transactionList }
    });
  }
}
